import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setConnected,
  setMicNotFound,
  setMicPermissionDenied,
  setRecording,
} from "@store/microphoneSlice";
import sleep from "@utils/sleep";
import { setMessage } from "@store/snackbarSlice";
import {
  VoiceDetectionEvent,
  VoiceDetector,
} from "@service/voice-detection/voice-detector";
import { VoiceDetectorFactory } from "@service/voice-detection/voice-detector.factory";
import { RootState } from "@store/store";
import { useTranslation } from "react-i18next";
import { MicStream } from "@service/mic-stream";

const MIC_CHECK_INTERVAL = 1000;

export const useRecordVoice = (
  processing: boolean,
  onVoiceRecorded: (blob: Blob | ArrayBufferLike) => void,
  streaming: boolean,
) => {
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const micStream = useRef<MicStream | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);
  const voiceDetector = useRef<VoiceDetector | null>(null);
  const chunks = useRef<Blob[]>([]);
  const dispatch = useDispatch();
  const muted = useSelector((state: RootState) => state.avatar.muted);
  const audioInputDeviceId =
    useSelector((state: RootState) => state.avatar.audioInputDeviceId) ?? "";
  const processingRef = useRef<boolean>(false);
  const { t } = useTranslation();
  const onVoiceRecordedRef = useRef(onVoiceRecorded);
  onVoiceRecordedRef.current = onVoiceRecorded;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    const audio = mediaStream.current?.getAudioTracks()?.[0];
    if (audio) {
      audio.enabled = !muted;
    }
  }, [muted]);

  const startRecording = useCallback((): void => {
    if (micStream.current) {
      micStream.current.start();
      dispatch(setRecording(true));
    }
    if (mediaRecorder.current && mediaRecorder.current.state !== "recording") {
      mediaRecorder.current.start();
      dispatch(setRecording(true));
    }
  }, [dispatch]);

  const stopRecording = useCallback((): void => {
    if (micStream.current) {
      micStream.current.stop();
      dispatch(setRecording(false));
    }
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      dispatch(setRecording(false));
    }
  }, [dispatch]);

  useEffect(() => {
    processingRef.current = processing;
    if (processing) {
      stopRecording();
    }
  }, [processing, stopRecording]);

  useEffect(() => {
    let cancelled = false;

    const cleanupPipeline = () => {
      if (mediaStream.current) {
        mediaStream.current.getAudioTracks().forEach((track) => track.stop());
        mediaStream.current = null;
      }
      if (voiceDetector.current) {
        voiceDetector.current.destroy();
        voiceDetector.current = null;
      }
      if (micStream.current) {
        void micStream.current.destroy();
        micStream.current = null;
      }
      mediaRecorder.current = null;
      dispatch(setRecording(false));
      dispatch(setConnected(false));
    };

    const getAudioStream = async (
      deviceId: string,
    ): Promise<MediaStream | null> => {
      const audioConstraints: MediaTrackConstraints | boolean = deviceId
        ? { deviceId: { exact: deviceId } }
        : true;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return null;
        }
        dispatch(setMicNotFound(false));
        dispatch(setMicPermissionDenied(false));
        return stream;
      } catch (e) {
        console.error(e);
        switch ((e as { name?: string })?.name) {
          case "NotFoundError": {
            dispatch(setMicNotFound(true));
            break;
          }
          case "NotAllowedError": {
            dispatch(setMicPermissionDenied(true));
            break;
          }
          default: {
            dispatch(
              setMessage({ content: t("errors.unknown"), severity: "error" }),
            );
            return null;
          }
        }
        await sleep(MIC_CHECK_INTERVAL);
        if (cancelled) {
          return null;
        }
        return getAudioStream(deviceId);
      }
    };

    const handleVoiceDetectionEvent = ({ type }: VoiceDetectionEvent) => {
      if (type === "start") {
        if (!processingRef.current) {
          startRecording();
        }
      } else {
        stopRecording();
      }
    };

    const initMediaRecorder = (stream: MediaStream): MediaRecorder => {
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (e) => {
        chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks.current, { type: "audio/wav" });
        onVoiceRecordedRef.current(audioBlob);
        chunks.current = [];
      };

      return recorder;
    };

    const run = async () => {
      cleanupPipeline();
      const stream = await getAudioStream(audioInputDeviceId);
      if (cancelled || !stream) {
        return;
      }
      mediaStream.current = stream;
      const track = stream.getAudioTracks()[0];
      if (track) {
        track.enabled = !mutedRef.current;
      }

      if (streaming) {
        micStream.current = new MicStream(stream, (blob) =>
          onVoiceRecordedRef.current(blob),
        );
      } else {
        mediaRecorder.current = initMediaRecorder(stream);
      }

      voiceDetector.current = VoiceDetectorFactory.create({
        stream,
        callback: handleVoiceDetectionEvent,
      });
      dispatch(setConnected(true));
    };

    void run();

    return () => {
      cancelled = true;
      cleanupPipeline();
    };
  }, [audioInputDeviceId, dispatch, startRecording, stopRecording, streaming, t]);

  return { startRecording, stopRecording };
};
